/**
 * Tooltip, Avatar, ProgressBar — the small indicators that were being rebuilt.
 *
 * ── Tooltip ────────────────────────────────────────────────────────────────
 * 2,113 native `title=""` attributes across 560 files stand in for a tooltip
 * today. `title` is not a tooltip: it never appears on touch devices, cannot be
 * styled, appears after an uncontrollable delay, and screen-reader support is
 * inconsistent. Most of those attributes are fine as plain labels — the ones
 * carrying information the user actually needs are what this is for.
 *
 * It shows on hover AND focus (a hover-only tooltip is unreachable by keyboard)
 * and is wired with aria-describedby rather than replacing the control's name.
 */
import React, { useId, useState } from 'react';
import { type Tone } from './foundation';

/* ── Tooltip ────────────────────────────────────────────────────────────── */

export interface TooltipProps {
  /** The tip text. Keep it short — this is not a place for a paragraph. */
  content: React.ReactNode;
  placement?: 'top' | 'bottom';
  /** The control being described. Must accept a ref-less span wrapper. */
  children: React.ReactNode;
  className?: string;
}

export function Tooltip({ content, placement = 'top', children, className = '' }: TooltipProps) {
  const [shown, setShown] = useState(false);
  const id = useId();

  return (
    <span
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setShown(true)}
      onMouseLeave={() => setShown(false)}
      onFocusCapture={() => setShown(true)}
      onBlurCapture={() => setShown(false)}
    >
      <span aria-describedby={shown ? id : undefined} className="inline-flex">
        {children}
      </span>
      {shown && (
        <span
          id={id}
          role="tooltip"
          className={`pointer-events-none absolute left-1/2 z-50 w-max max-w-[22rem] -translate-x-1/2 border bg-[var(--surface-raised)] px-2.5 py-1.5 text-xs leading-snug text-[var(--text-pri)] ${
            placement === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
          }`}
          style={{ borderColor: 'var(--rule-bright)' }}
        >
          {content}
        </span>
      )}
    </span>
  );
}

/* ── Avatar ─────────────────────────────────────────────────────────────── */

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

const AVATAR_SIZE: Record<AvatarSize, string> = {
  xs: 'h-5 w-5 text-[0.6rem]',
  sm: 'h-6 w-6 text-[0.65rem]',
  md: 'h-8 w-8 text-xs',
  lg: 'h-10 w-10 text-sm',
};

export interface AvatarProps {
  /** Full name. Initials are derived from it, and it is the accessible name. */
  name: string;
  src?: string;
  size?: AvatarSize;
  /** Ring in a semantic tone — for presence or state, not decoration. */
  ring?: Tone;
  className?: string;
}

/** First and last initial. Deliberately not the first two letters: "Okonkwo"
    should read OK only if there is no second name to take a letter from. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Avatar({ name, src, size = 'md', ring, className = '' }: AvatarProps) {
  return (
    <span
      title={undefined}
      aria-label={name}
      role="img"
      className={`inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[var(--surface-raised)] font-mono uppercase tracking-[0.04em] text-[var(--text-ter)] ${AVATAR_SIZE[size]} ${className}`}
      style={
        ring
          ? { boxShadow: `0 0 0 1.5px ${ring === 'neutral' ? 'var(--rule-bright)' : `var(--${ring})`}` }
          : undefined
      }
    >
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
}

export interface AvatarGroupProps {
  names: string[];
  size?: AvatarSize;
  /** Show at most this many, then a +N chip. */
  max?: number;
  className?: string;
}

export function AvatarGroup({ names, size = 'sm', max = 4, className = '' }: AvatarGroupProps) {
  const shown = names.slice(0, max);
  const overflow = names.length - shown.length;
  return (
    <span className={`inline-flex items-center ${className}`}>
      {shown.map((n, i) => (
        <Avatar key={`${n}-${i}`} name={n} size={size} className={i > 0 ? '-ml-1.5' : ''} />
      ))}
      {overflow > 0 && (
        <span
          className={`-ml-1.5 inline-flex shrink-0 items-center justify-center rounded-full bg-[var(--surface)] font-mono text-[var(--text-dim)] ${AVATAR_SIZE[size]}`}
        >
          +{overflow}
        </span>
      )}
    </span>
  );
}

/* ── ProgressBar ────────────────────────────────────────────────────────── */

export interface ProgressBarProps {
  value: number;
  max?: number;
  tone?: Tone;
  /** Mono label above the bar. */
  label?: React.ReactNode;
  /** Render the value as text beside the label. */
  showValue?: boolean;
  /** Format the readout. Default is "value / max". */
  format?: (value: number, max: number) => string;
  size?: 'sm' | 'md';
  className?: string;
}

const TONE_BG: Record<Tone, string> = {
  green: 'bg-[var(--green)]',
  mustard: 'bg-[var(--mustard)]',
  slate: 'bg-[var(--slate)]',
  coral: 'bg-[var(--coral)]',
  neutral: 'bg-[var(--text-dim)]',
};

export function ProgressBar({
  value,
  max = 100,
  tone = 'slate',
  label,
  showValue = false,
  format,
  size = 'md',
  className = '',
}: ProgressBarProps) {
  const safeMax = max > 0 ? max : 1;
  const clamped = Math.min(Math.max(value, 0), safeMax);
  const pct = (clamped / safeMax) * 100;
  const readout = format ? format(clamped, safeMax) : `${clamped} / ${safeMax}`;

  return (
    <div className={className}>
      {(label || showValue) && (
        <div className="mb-1.5 flex items-baseline justify-between gap-3">
          {label && (
            <span className="font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">
              {label}
            </span>
          )}
          {showValue && (
            <span className="font-mono text-xs tabular-nums text-[var(--text-sec)]">{readout}</span>
          )}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-label={typeof label === 'string' ? label : undefined}
        className={`w-full overflow-hidden bg-[var(--surface)] ${size === 'sm' ? 'h-1' : 'h-1.5'}`}
      >
        <div
          className={`h-full transition-[width] duration-300 ${TONE_BG[tone]}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
